import { useEffect, useState } from "react";
import { Locataire } from "../../model/Model";
import { getLocataireById } from "../../service/locataires/locataires.service";

interface AjouterLocatairePopupProps {
    ajouterModifier: (locataire: Locataire) => Promise<void>;
    onClose?: () => void;
    locataireId: number | undefined;
}

export function AjouterLocatairePopup({ ajouterModifier, onClose, locataireId }: AjouterLocatairePopupProps) {
    const [locataire, setLocataire] = useState<Locataire>({
        locataireId: null,
        nom: "",
        prenom: "",
        dateDeNaissance: "",
        telephone: "",
        email: "",
        adresse: ""
    });

    useEffect(() => {
        const fetchLocataire = async () => {
            if (!locataireId) return;
            const data = await getLocataireById(locataireId);
            if (data) {
                setLocataire(data);
            }
        };
        fetchLocataire();
    }, [locataireId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await ajouterModifier(locataire);
        // Reset form
        setLocataire({
            locataireId: null,
            nom: "",
            prenom: "",
            dateDeNaissance: "",
            telephone: "",
            email: "",
            adresse: ""
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLocataire(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {locataireId ? "Modifier le locataire" : "Ajouter un locataire"}
                    </h3>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom *
                        </label>
                        <input
                            type="text"
                            name="nom"
                            value={locataire.nom}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nom du locataire"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prénom *
                        </label>
                        <input
                            type="text"
                            name="prenom"
                            value={locataire.prenom}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Prénom du locataire"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date de naissance
                            </label>
                            <input
                                type="date"
                                name="dateDeNaissance"
                                value={locataire.dateDeNaissance}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Téléphone
                            </label>
                            <input
                                type="tel"
                                name="telephone"
                                value={locataire.telephone}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Numéro de téléphone"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={locataire.email}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Adresse email"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adresse
                        </label>
                        <input
                            type="text"
                            name="adresse"
                            value={locataire.adresse}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Adresse complète"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-200"
                            >
                                Annuler
                            </button>
                        )}
                        {locataireId ? (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition duration-200"
                            >
                                Modifier
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition duration-200"
                            >
                                Ajouter
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}